import { useEffect } from 'react'
import { type Flag, type FeatureFlag, useFeatureFlagWithPrefs } from '@fs/zion-flags'

function getCachedFeatureFlag<F extends Flag<object>>(cacheKey: string): FeatureFlag<F> | undefined {
  try {
    const raw = localStorage.getItem(cacheKey)
    if (raw !== null) return JSON.parse(raw) as FeatureFlag<F>
  } catch {
    // ignore
  }
  return undefined
}

// Wraps useFeatureFlagWithPrefs and hydrates data (including treatment) from localStorage while
// the flag is loading, so data?.isOn and data?.treatment are immediately available without
// needing to gate on loading for layout or rendering decisions.
export default function useCachedFeatureFlagWithPrefs<F extends Flag<object>>(
  flagName: string,
  cacheKey: string,
  options: Parameters<typeof useFeatureFlagWithPrefs<F>>[2]
): ReturnType<typeof useFeatureFlagWithPrefs<F>> {
  const { data, loading, error } = useFeatureFlagWithPrefs<F>(flagName, {}, options)

  useEffect(() => {
    if (!loading && data) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(data))
      } catch {
        // ignore
      }
    }
  }, [cacheKey, data, loading])

  return {
    data: loading ? getCachedFeatureFlag<F>(cacheKey) : data,
    loading,
    error,
  }
}
